import { getGaMeasurementId } from "@/lib/ga.functions";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/** Identyfikator pomiaru GA4, dopóki nie zostanie wczytany: null. */
let measurementId: string | null = null;
/** Czy inicjalizacja już wystartowała (żebym nie wstawiała skryptu dwa razy). */
let started = false;

/** Ścieżka, dla której ostatnio wysłano page_view (null w trakcie nawigacji). */
let lastPageViewPath: string | null = null;
const pageViewListeners = new Set<(path: string) => void>();

function notifyPageViewSent(path: string): void {
  lastPageViewPath = path;
  pageViewListeners.forEach((fn) => {
    try {
      fn(path);
    } catch {
      // analityka nie może blokować strony
    }
  });
}

/**
 * Wywołuje fn dokładnie raz, zaraz po wysłaniu page_view dla podanej ścieżki
 * (jeśli już został wysłany, od razu). Zwraca funkcję anulującą.
 */
export function afterPageView(path: string, fn: () => void): () => void {
  if (lastPageViewPath === path) {
    fn();
    return () => undefined;
  }
  const listener = (sent: string) => {
    if (sent !== path) return;
    pageViewListeners.delete(listener);
    fn();
  };
  pageViewListeners.add(listener);
  return () => pageViewListeners.delete(listener);
}

/** Wkleja skrypt gtag.js i konfiguruje GA4, dokładnie wg kodu od Google. */
function injectGtag(id: string): void {
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  // Dokładnie jak w oficjalnym snippetie Google: do dataLayer trafia obiekt
  // arguments, a nie tablica zbudowana z parametrów, żeby gtag.js na pewno
  // rozpoznał każdy rozkaz ("js", "config", "event", ...) w swoim formacie.
  const gtag: (...args: unknown[]) => void = function () {
    window.dataLayer?.push(arguments);
  };
  window.gtag = gtag;

  gtag("js", new Date());
  gtag("config", id);
  // config wysyła pierwszy page_view; kolejne komendy gtag trafiają do kolejki po nim
  notifyPageViewSent(window.location.pathname);
}

/** Produkcyjne hosty, na których działa pomiar GA4. */
const PRODUCTION_ANALYTICS_HOSTS = new Set(["sylwiasitkowska.pl", "www.sylwiasitkowska.pl"]);

/** Czy pomiar GA4/GTM ma działać na podanym hoście (tylko produkcja, bez www i z www). */
export function isProductionAnalyticsHost(hostname: string): boolean {
  return PRODUCTION_ANALYTICS_HOSTS.has(hostname);
}

/**
 * Podpina Google Analytics 4 (gtag.js) w przeglądarce.
 * Identyfikator pomiaru pobiera z sekretów projektu przez funkcję serwerową,
 * więc w kodzie strony nie ma go na sztywno. Bezpieczna do wielokrotnego wywołania.
 * Na hostach podglądu i localhost nie robi nic: żadnego skryptu, żadnego pomiaru.
 */
export async function initGa(): Promise<void> {
  if (typeof window === "undefined" || started) return;
  started = true;
  if (!isProductionAnalyticsHost(window.location.hostname)) return;
  try {
    const { measurementId: id } = await getGaMeasurementId();
    if (!id) return;
    measurementId = id;
    injectGtag(id);
    watchPathChanges();
  } catch {
    // analityka nie może blokować działania strony
  }
}

/** page_view po nawigacji w obrębie aplikacji (SPA); pierwsze odsłony nie wysyłać. */
export function trackGaPageView(path: string = window.location.pathname): void {
  if (!measurementId) return;
  try {
    window.gtag?.("event", "page_view", {
      page_location: window.location.href,
      page_path: path,
      page_title: document.title,
    });
    notifyPageViewSent(path);
  } catch {
    // analityka nie może blokować nawigacji
  }
}

/**
 * Reaguje na zmiany adresu przy przejściach w obrębie aplikacji (SPA):
 * nasłuchuje historii przeglądarki, tak samo jak atrybucja kampanii.
 */
function watchPathChanges(): void {
  let last = window.location.pathname;
  let pending: (() => void) | null = null;

  const check = () => {
    if (window.location.pathname === last) return;
    last = window.location.pathname;
    const path = last;
    lastPageViewPath = null;
    const previousTitle = document.title;

    // anuluj oczekiwanie z poprzedniej, niedokończonej nawigacji (jedno page_view na wyświetlenie)
    pending?.();

    // page_view dopiero, gdy tytuł dokumentu należy do nowej podstrony;
    // jeśli tytuł się nie zmienia (np. identyczny), wysyłka po krótkim limicie czasu
    let done = false;
    const observer = new MutationObserver(() => {
      if (document.title !== previousTitle) fire();
    });
    const timer = window.setTimeout(() => fire(), 1500);
    function cleanup() {
      done = true;
      observer.disconnect();
      window.clearTimeout(timer);
      pending = null;
    }
    function fire() {
      if (done) return;
      cleanup();
      if (window.location.pathname !== path) return;
      trackGaPageView(path);
    }
    pending = cleanup;
    observer.observe(document.head, { subtree: true, childList: true, characterData: true });
  };
  window.addEventListener("popstate", check);
  const originalPushState = history.pushState.bind(history);
  history.pushState = function patchedPushState(
    ...args: Parameters<History["pushState"]>
  ) {
    originalPushState(...args);
    check();
  };
}
